import { Token } from './types';

const isWhitespace = (char: string | undefined): boolean =>
  char !== undefined && /\s/.test(char);

const isTokenBoundary = (char: string): boolean =>
  isWhitespace(char) || char === '(' || char === ')' || char === '"';

const normalizeQuotedValue = (raw: string): string =>
  raw.trim().replace(/\s+/g, ' ');

const classifyWord = (word: string): Token => {
  const lowered = word.toLowerCase();

  if (lowered === 'and' || word === '&&') {
    return { type: 'and' };
  }

  if (lowered === 'or' || word === '||') {
    return { type: 'or' };
  }

  if (lowered === 'not') {
    return { type: 'not' };
  }

  return { type: 'term', value: word };
};

export const tokenize = (input: string): Token[] => {
  const tokens: Token[] = [];
  const { length } = input;
  let index = 0;

  while (index < length) {
    const char = input[index];

    if (isWhitespace(char)) {
      index += 1;
    } else if (char === '(') {
      tokens.push({ type: 'lparen' });
      index += 1;
    } else if (char === ')') {
      tokens.push({ type: 'rparen' });
      index += 1;
    } else if (char === '"') {
      index += 1;
      const start = index;

      while (index < length && input[index] !== '"') {
        index += 1;
      }

      const raw = input.slice(start, index);

      if (index < length) {
        index += 1;
      }

      const normalized = normalizeQuotedValue(raw);

      if (normalized !== '') {
        tokens.push({ type: 'term', value: normalized });
      }
    } else {
      const previousChar = index === 0 ? undefined : input[index - 1];
      const nextChar = input[index + 1];
      const precededByTokenStart =
        index === 0 || isWhitespace(previousChar) || previousChar === '(';
      const followedByNonWhitespace =
        nextChar !== undefined && !isWhitespace(nextChar);
      const isNegationDash =
        char === '-' && precededByTokenStart && followedByNonWhitespace;
      const isDanglingDash =
        char === '-' && precededByTokenStart && !followedByNonWhitespace;

      if (isNegationDash) {
        tokens.push({ type: 'not' });
        index += 1;
      } else if (isDanglingDash) {
        index += 1;
      } else {
        const start = index;

        while (index < length && !isTokenBoundary(input[index])) {
          index += 1;
        }

        tokens.push(classifyWord(input.slice(start, index)));
      }
    }
  }

  return tokens;
};
